import {isHLSProvider, MediaPlayer, MediaProvider} from '@vidstack/react'
import {useEffect, useId, useRef, useState} from 'react'

import {twMerge} from '@sdox/utils'
import {getTrackingProps} from '@sdox/utils/dom'

import {usePlayersStore} from '../../hooks/usePlayersStore'
import {VideoLayout} from './VideoLayout'
import {Overlay} from './parts/Overlay'
import {useIOS} from '../../hooks'

import type {TrackingProps} from '@sdox/utils/dom'
import type {MediaPlayerInstance, MediaProviderAdapter, MediaProviderChangeEvent} from '@vidstack/react'
import type {VideoType} from '../Video/VideoTypes'

import '@vidstack/react/player/styles/base.css'
import './player.scss'

export interface PlayerProps extends VideoType {
  id?: string
  className?: string
  tracking?: TrackingProps
  isVideoObjectCover?: boolean
}

export const Player = ({
  autoplay,
  className,
  id,
  loop,
  meta,
  muted,
  noControls,
  isVideoObjectCover,
  standalone,
  thumbnail,
  tracking,
  url,
}: PlayerProps) => {
  let playerId = useId()
  playerId = id ?? playerId
  const player = useRef<MediaPlayerInstance>(null)
  const {addPlayer, pauseOtherPlayers, removePlayer} = usePlayersStore()
  const [isLoading, setIsLoading] = useState(false)
  const isIOS = useIOS()

  const handlePlay = () => {
    if (player.current) {
      setIsLoading(true)
      player.current.startLoading()
      player.current.listen('can-play', () => player.current?.play(), {once: true})
      player.current.listen('playing', () => setIsLoading(false), {once: true})
    }
  }

  useEffect(() => {
    const currentPlayer = player.current
    const pausePlayer = () => currentPlayer?.pause()
    const playPlayer = () => currentPlayer?.play()

    if (currentPlayer) {
      if (!standalone) {
        addPlayer(playerId, currentPlayer)
        currentPlayer.listen('play', () => pauseOtherPlayers(playerId))
      }
      if (loop && muted) {
        window.addEventListener('blur', pausePlayer)
        window.addEventListener('focus', playPlayer)
      }
    }

    return () => {
      if (currentPlayer) {
        removePlayer(playerId)
        window.removeEventListener('blur', pausePlayer)
        window.removeEventListener('focus', playPlayer)
      }
    }
    // Just run it once on mount!
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const onProviderChange = (
    provider: MediaProviderAdapter | null,
    _: MediaProviderChangeEvent,
  ) => {
    if (isHLSProvider(provider)) {
      provider.library = () => import('hls.js')
    }
  }

  return (
    <MediaPlayer
      ref={player}
      title={meta?.title ?? ''}
      src={url}
      crossOrigin
      playsInline
      autoPlay={autoplay}
      muted={isIOS ? true : muted}
      loop={loop}
      load={autoplay ? 'eager' : 'custom'}
      posterLoad={thumbnail ? 'visible' : 'idle'}
      controls={false}
      onPlayFail={handlePlay}
      className={twMerge(
        'overflow-hidden ring-interactive-glyph-brand-default data-[focus]:ring-4',
        isVideoObjectCover ? 'size-full' : 'aspect-[16/9] w-full',
        className,
      )}
      onProviderChange={onProviderChange}
    >
      <MediaProvider className={isVideoObjectCover ? '[&>video]:size-full [&>video]:object-cover' : ''}>
        <Overlay
          title={meta?.title}
          noControls={noControls}
          thumbnail={thumbnail}
          handlePlay={handlePlay}
          isLoading={isLoading}
        />
      </MediaProvider>
      {!noControls ? <VideoLayout playButtonProps={getTrackingProps(tracking)} /> : null}
    </MediaPlayer>
  )
}
